/**
 * Los iconos de la aplicación. UN SOLO JUEGO Y UN SOLO SITIO.
 *
 * Son Tabler (`@tabler/icons-react`), y aquí se les ponen NUESTROS valores por
 * defecto. El resto del código importa siempre de este archivo y nunca del
 * paquete: eso es lo que hace que cambiar de juego de iconos, o subir el
 * grosor del trazo, sea tocar un archivo y no ciento.
 *
 * Dos juegos conviviendo son dos lenguajes visuales --trazos, remates y
 * rejilla distintos-- y se nota en cuanto dos iconos quedan cerca. Por eso no
 * queda ni uno de `lucide-react`, tampoco en las primitivas que vinieron de
 * shadcn: son nuestras desde que se copiaron al repositorio.
 *
 * Se importa por nombre, nunca el paquete entero (`import * as`): Tabler son
 * miles de iconos y solo deben acabar en el bundle los que se usan.
 */
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowLeft,
  IconArrowUp,
  IconBriefcase,
  IconChartBar,
  IconChartHistogram,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconCircle,
  IconCircleCheck,
  IconCircleDot,
  IconCoins,
  IconDots,
  IconDownload,
  IconFileText,
  IconGauge,
  IconInbox,
  IconLayoutDashboard,
  IconLayoutSidebar,
  IconLayoutSidebarLeftCollapse,
  IconLink,
  IconLoader2,
  IconLock,
  IconLogin,
  IconLogout,
  IconMapPin,
  IconMessage,
  IconMicrophone,
  IconMicrophoneOff,
  IconPaperclip,
  IconPencil,
  IconPlus,
  IconSelector,
  IconSettings,
  IconSquare,
  IconTable,
  IconTrash,
  IconUser,
  IconUsers,
  IconX,
  type IconProps,
} from "@tabler/icons-react";
import { createElement, type FunctionComponent } from "react";

/**
 * A qué tamaño y con qué trazo se dibujan.
 *
 * 16px es el tamaño que ya asumen los botones (`size-4`). El tamaño va como
 * atributo y no como clase A PROPÓSITO: así una clase `size-*` --la que el
 * botón pone según su tamaño, o la que ponga quien coloque el icono-- sigue
 * mandando sobre esto, que es solo el valor de partida.
 */
const SIZE = 16;
const STROKE = 2;

type IconComponent = FunctionComponent<IconProps>;

const withDefaults = (Icon: IconComponent, name: string): IconComponent => {
  const Styled: IconComponent = (props) =>
    createElement(Icon, { size: SIZE, stroke: STROKE, ...props });
  Styled.displayName = name;
  return Styled;
};

/* Navegación y estructura. */
export const ArrowLeftIcon = withDefaults(IconArrowLeft, "ArrowLeftIcon");
export const ArrowUpIcon = withDefaults(IconArrowUp, "ArrowUpIcon");
export const ArrowDownIcon = withDefaults(IconArrowDown, "ArrowDownIcon");
export const ChevronLeftIcon = withDefaults(IconChevronLeft, "ChevronLeftIcon");
export const ChevronRightIcon = withDefaults(
  IconChevronRight,
  "ChevronRightIcon",
);
export const ChevronDownIcon = withDefaults(IconChevronDown, "ChevronDownIcon");
export const ChevronUpIcon = withDefaults(IconChevronUp, "ChevronUpIcon");
/** El de ordenar una columna que todavía no ordena por nada. */
export const SortIcon = withDefaults(IconSelector, "SortIcon");
export const SidebarShowIcon = withDefaults(
  IconLayoutSidebar,
  "SidebarShowIcon",
);
export const SidebarHideIcon = withDefaults(
  IconLayoutSidebarLeftCollapse,
  "SidebarHideIcon",
);

/* Acciones. */
export const PlusIcon = withDefaults(IconPlus, "PlusIcon");
export const CloseIcon = withDefaults(IconX, "CloseIcon");
export const CheckIcon = withDefaults(IconCheck, "CheckIcon");
export const AttachIcon = withDefaults(IconPaperclip, "AttachIcon");
export const DownloadIcon = withDefaults(IconDownload, "DownloadIcon");
export const LinkIcon = withDefaults(IconLink, "LinkIcon");
/** Detener el turno: un cuadrado, como en cualquier reproductor. */
export const StopIcon = withDefaults(IconSquare, "StopIcon");
export const SettingsIcon = withDefaults(IconSettings, "SettingsIcon");

/* Los dos modos de trabajo (§9.2). */
export const ChatIcon = withDefaults(IconMessage, "ChatIcon");
export const WorkIcon = withDefaults(IconLayoutDashboard, "WorkIcon");

/* Las secciones del panel. */
export const OverviewIcon = withDefaults(IconGauge, "OverviewIcon");
export const ParticipantsIcon = withDefaults(IconUsers, "ParticipantsIcon");
export const KpisIcon = withDefaults(IconChartBar, "KpisIcon");
export const PositionsIcon = withDefaults(IconBriefcase, "PositionsIcon");
export const RegionsIcon = withDefaults(IconMapPin, "RegionsIcon");

/* Alertas: la severidad se lee antes de leer el texto. */
export const WarningIcon = withDefaults(IconAlertTriangle, "WarningIcon");
export const InfoIcon = withDefaults(IconCircleDot, "InfoIcon");

/* Consumo de tokens y su coste estimado (2.4). */
export const UsageIcon = withDefaults(IconCoins, "UsageIcon");

/* Lo que se enseña cuando no hay nada que enseñar. */
export const EmptyIcon = withDefaults(IconInbox, "EmptyIcon");
/** Cuando no hay nada que mirar y eso es una buena noticia, no un hueco. */
export const OkIcon = withDefaults(IconCircleCheck, "OkIcon");

/* Lo que anuncia a un componente del agente que no cabe donde está (§9). */
export const TableIcon = withDefaults(IconTable, "TableIcon");
export const ChartIcon = withDefaults(IconChartHistogram, "ChartIcon");
export const DocumentIcon = withDefaults(IconFileText, "DocumentIcon");

/* El menú de una fila del historial: renombrar y borrar. */
export const MoreIcon = withDefaults(IconDots, "MoreIcon");
export const RenameIcon = withDefaults(IconPencil, "RenameIcon");
export const DeleteIcon = withDefaults(IconTrash, "DeleteIcon");
/** La marca redonda de un radio: se rellena con `fill-current`. */
export const DotIcon = withDefaults(IconCircle, "DotIcon");

/* Dictado por voz. */
export const MicIcon = withDefaults(IconMicrophone, "MicIcon");
export const MicOffIcon = withDefaults(IconMicrophoneOff, "MicOffIcon");
/** El que gira mientras se transcribe. */
export const SpinnerIcon = withDefaults(IconLoader2, "SpinnerIcon");

/* La sesión. */
export const SignInIcon = withDefaults(IconLogin, "SignInIcon");
export const SignOutIcon = withDefaults(IconLogout, "SignOutIcon");
/** Quien está dentro, cuando no hay de dónde sacar sus iniciales. */
export const UserIcon = withDefaults(IconUser, "UserIcon");
/** Acceso denegado: la aplicación, o una acción. */
export const LockIcon = withDefaults(IconLock, "LockIcon");
